import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { Box, Typography } from '@mui/material'
import React from 'react'

import { HeyaHeroBackground, type HeyaHeroVariant } from './heyaHeroBackground'

/** The resolved strings a page passes down; also the shape callers use to signal "Heya". */
export interface HeyaHeroCopy {
    badge: string
    headingLead: string
    headingAccent: string
    intro: string
}

interface HeyaGreetingProps extends HeyaHeroCopy {
    variant?: HeyaHeroVariant
}

/**
 * Heya hero copy — shared by the flights and hotels landing pages, which differ only in
 * their artwork and wording. It lives here rather than under flightGreeting/ so hotels does
 * not import from a flights-named folder.
 *
 * Mirrors the xs/lg split the Flyin greeting uses: this component owns the mobile backdrop,
 * and the caller owns the desktop one (where it must sit behind the search tabs too).
 */
export const HeyaGreeting: React.FC<HeyaGreetingProps> = ({
    badge,
    headingLead,
    headingAccent,
    intro,
    variant = 'flights'
}) => {
    return (
        <Box
            sx={{
                position: 'relative',
                pb: { xs: 3, lg: 0 },
                pt: { xs: 3, lg: 6 }
            }}
        >
            <HeyaHeroBackground variant={variant} display={{ xs: 'block', lg: 'none' }} />

            {/* On mobile this greeting sits outside FlightContainer, so it has no gutter of
                its own — px matches the 16px inset of the search card below it. The backdrop
                stays full-bleed because it is positioned against the padding box. */}
            <Box sx={{ position: 'relative', px: { xs: 2, lg: 0 }, maxWidth: { xs: '100%', lg: 680 } }}>
                <Box
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        mb: { xs: 1.5, lg: 3 },
                        px: 1.75,
                        py: 0.75,
                        borderRadius: 999,
                        bgcolor: 'primary.100',
                        border: { xs: 'none', lg: '1px solid' },
                        borderColor: 'secondary.main',
                        color: 'primary.main'
                    }}
                >
                    <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                    <Typography component="span" sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1 }}>
                        {badge}
                    </Typography>
                </Box>

                <Typography
                    component="h1"
                    variant="h3"
                    sx={{
                        mb: { xs: 1.5, md: 2 },
                        fontWeight: 600,
                        fontSize: { xs: '1.5rem', lg: '3.5rem' },
                        lineHeight: 1.1,
                        color: 'text.primary',
                        width: { xs: '80%', lg: 'auto' },
                        // Desktop keeps lead + accent on one line; the copy column is capped at
                        // 680 for the intro paragraph, so the heading is allowed to run past it.
                        whiteSpace: { xs: 'normal', lg: 'nowrap' }
                    }}
                >
                    {headingLead}{' '}
                    <Typography component="span" sx={{ color: 'primary.main', fontSize: 'inherit', fontWeight: 'inherit' }}>
                        {headingAccent}
                    </Typography>
                </Typography>

                <Typography
                    component="p"
                    sx={{
                        fontWeight: 300,
                        fontSize: { xs: 15, lg: 18 },
                        lineHeight: 1.5,
                        color: '#4B5563', //keep it hardcoded as it is a specific shade of gray, not the default text.secondary
                        display: { xs: 'none', lg: 'block' }
                    }}
                >
                    {intro}
                </Typography>
            </Box>
        </Box>
    )
}
